import React, { useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Trash2, Plus } from 'lucide-react'
import { useFieldArray, useForm } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import type { CustomQuestion } from '@/lib/widget-schema'
import { useTranslation } from '@/hooks/useTranslation'

interface CustomQuestionBuilderProps {
	questions: CustomQuestion[]
	onChange: (questions: CustomQuestion[]) => void
}

function createEmptyQuestion(): CustomQuestion {
	return {
		id: uuidv4(),
		type: 'text',
		question: '',
		required: true,
		order_index: 0,
		options: [],
		min_scale: 1,
		max_scale: 5,
	}
}

export function CustomQuestionBuilder({
	questions,
	onChange,
}: CustomQuestionBuilderProps) {
	const { t } = useTranslation()
	const { control, watch, setValue, getValues, reset, trigger } = useForm<{
		questions: CustomQuestion[]
	}>({
		defaultValues: {
			questions: [],
		},
	})

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'questions',
	})

	useEffect(() => {
		const currentInternalQuestions = getValues('questions')
		if (
			JSON.stringify(questions) !== JSON.stringify(currentInternalQuestions)
		) {
			const initialData =
				questions && questions.length > 0
					? questions.map((q, i) => ({
							...q,
							id: q.id || uuidv4(),
							order_index: i,
					  }))
					: [createEmptyQuestion()]
			reset({ questions: initialData })
		}
	}, [questions, reset, getValues])

	const propagateChange = useCallback(() => {
		trigger()
		const currentQuestions = getValues('questions').map((q, index) => ({
			...q,
			order_index: index,
		}))
		onChange(currentQuestions)
	}, [getValues, onChange, trigger])

	return (
		<div className="space-y-4">
			{fields.map((fieldItem, index) => (
				<Card key={fieldItem.id}>
					<CardContent className="pt-6 space-y-4">
						<div className="flex items-start justify-between gap-4">
							<FormField
								control={control}
								name={`questions.${index}.question`}
								rules={{
									required: t('widgetBuilder.customQuestionBuilder.questionRequiredError'),
								}}
								render={({ field, fieldState }) => (
									<FormItem className="flex-1">
										<FormLabel>
											{t('widgetBuilder.customQuestionBuilder.questionLabel', {
												index: index + 1,
											})}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder={t('widgetBuilder.customQuestionBuilder.questionPlaceholder')}
												className={fieldState.error ? 'border-red-500' : ''}
												onBlur={() => {
													field.onBlur()
													propagateChange()
												}}
											/>
										</FormControl>
										{fieldState.error && (
											<FormMessage>{fieldState.error.message}</FormMessage>
										)}
									</FormItem>
								)}
							/>
							<FormField
								control={control}
								name={`questions.${index}.type`}
								render={({ field }) => (
									<FormItem className="w-[180px]">
										<FormLabel>{t('widgetBuilder.customQuestionBuilder.typeLabel')}</FormLabel>
										<Select
											value={field.value}
											onValueChange={(value) => {
												field.onChange(value)
												if (value !== 'multiple_choice') {
													setValue(`questions.${index}.options`, [])
												} else if (
													!getValues(`questions.${index}.options`)?.length
												) {
													setValue(`questions.${index}.options`, [''])
												}
												if (value !== 'scale') {
													setValue(`questions.${index}.min_scale`, 1)
													setValue(`questions.${index}.max_scale`, 5)
												}
												propagateChange()
											}}>
											<FormControl>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="text">
													{t('widgetBuilder.customQuestionBuilder.typeText')}
												</SelectItem>
												<SelectItem value="scale">
													{t('widgetBuilder.customQuestionBuilder.typeScale')}
												</SelectItem>
												<SelectItem value="email">
													{t('widgetBuilder.customQuestionBuilder.typeEmail')}
												</SelectItem>
												<SelectItem value="multiple_choice">
													{t('widgetBuilder.customQuestionBuilder.typeMultipleChoice')}
												</SelectItem>
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="mt-6"
								onClick={() => {
									remove(index)
									propagateChange()
								}}
								disabled={fields.length === 1 && fields[0].question === ''}>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>

						{watch(`questions.${index}.type`) === 'multiple_choice' && (
							<div className="space-y-2">
								<FormLabel>{t('widgetBuilder.customQuestionBuilder.optionsLabel')}</FormLabel>
								{(watch(`questions.${index}.options`) || ['']).map(
									(_option, optionIndex) => (
										<div key={optionIndex} className="flex gap-2">
											<FormField
												control={control}
												name={`questions.${index}.options.${optionIndex}`}
												rules={{
													required: t(
														'widgetBuilder.customQuestionBuilder.optionRequiredError',
													),
												}}
												render={({ field, fieldState }) => (
													<FormItem className="flex-1">
														<FormControl>
															<Input
																{...field}
																placeholder={t(
																	'widgetBuilder.customQuestionBuilder.optionPlaceholder',
																	{ index: optionIndex + 1 },
																)}
																className={
																	fieldState.error ? 'border-red-500' : ''
																}
																onBlur={() => {
																	field.onBlur()
																	propagateChange()
																}}
															/>
														</FormControl>
														{fieldState.error && (
															<FormMessage>
																{fieldState.error.message}
															</FormMessage>
														)}
													</FormItem>
												)}
											/>
											{(watch(`questions.${index}.options`) || []).length >
												1 && (
												<Button
													type="button"
													variant="ghost"
													size="icon"
													onClick={() => {
														const currentOptions =
															getValues(`questions.${index}.options`) || []
														const newOptions = [...currentOptions]
														newOptions.splice(optionIndex, 1)
														setValue(`questions.${index}.options`, newOptions)
														propagateChange()
													}}>
													<Trash2 className="h-4 w-4" />
												</Button>
											)}
										</div>
									)
								)}
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => {
										const currentOptions =
											getValues(`questions.${index}.options`) || []
										setValue(`questions.${index}.options`, [
											...currentOptions,
											'',
										])
										propagateChange()
									}}>
									<Plus className="mr-2 h-4 w-4" />{' '}
									{t('widgetBuilder.customQuestionBuilder.addOption')}
								</Button>
							</div>
						)}

						{watch(`questions.${index}.type`) === 'scale' && (
							<div className="grid grid-cols-2 gap-4">
								<FormField
									control={control}
									name={`questions.${index}.min_scale`}
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('widgetBuilder.customQuestionBuilder.minScale')}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													{...field}
													value={field.value ?? 1}
													onChange={(e) => {
														field.onChange(parseInt(e.target.value, 10) || 1)
														propagateChange()
													}}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
								<FormField
									control={control}
									name={`questions.${index}.max_scale`}
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('widgetBuilder.customQuestionBuilder.maxScale')}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													{...field}
													value={field.value ?? 5}
													onChange={(e) => {
														field.onChange(parseInt(e.target.value, 10) || 5)
														propagateChange()
													}}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							</div>
						)}
					</CardContent>
				</Card>
			))}

			<Button
				variant="outline"
				type="button"
				onClick={() => {
					const newQuestion = createEmptyQuestion()
					newQuestion.order_index = fields.length
					append(newQuestion)
					propagateChange()
				}}>
				<Plus className="mr-2 h-4 w-4" />{' '}
				{t('widgetBuilder.customQuestionBuilder.addQuestion')}
			</Button>
		</div>
	)
}
